import { useState, useEffect } from 'react';
import { BACKEND_URL } from '@/lib/env';
import axios from 'axios';
import { Activity, CheckCircle, Clock, AlertTriangle, Cpu, FileCheck } from 'lucide-react';
import { usePermissions } from '@/contexts/PermissionsContext';

const API = `${BACKEND_URL}/api`;

export default function Dashboard() {
  const { hasPermission } = usePermissions();
  const canReadDashboard = hasPermission('dashboard', 'read');
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMetrics();
  }, [canReadDashboard]);

  const fetchMetrics = async () => {
    if (!canReadDashboard) {
      setMetrics(null);
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(`${API}/dashboard/metrics`, { timeout: 8000 });
      setMetrics(response.data);
    } catch (error) {
      console.error('Error fetching metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full text-cp-muted">
        Loading…
      </div>
    );
  }

  const metricCards = [
    {
      title: 'Total Tickets',
      value: metrics?.total_tickets || 0,
      icon: Activity,
      description: 'All customization requests',
      accent: 'var(--indigo)',
      numClass: 'text-cp-indigo',
    },
    {
      title: 'Active Tickets',
      value: metrics?.active_tickets || 0,
      icon: Clock,
      description: 'Currently in progress',
      accent: 'var(--mod)',
      numClass: 'text-cp-mod',
    },
    {
      title: 'Completed',
      value: metrics?.completed_tickets || 0,
      icon: CheckCircle,
      description: 'Successfully delivered',
      accent: 'var(--ok)',
      numClass: 'text-cp-ok',
    },
    {
      title: 'Active Agents',
      value: metrics?.active_agents || 0,
      icon: Cpu,
      description: 'AI agents working',
      accent: 'var(--violet)',
      numClass: 'text-cp-violet',
    },
    {
      title: 'Pending Approvals',
      value: metrics?.pending_approvals || 0,
      icon: AlertTriangle,
      description: 'Awaiting governance review',
      accent: 'var(--imp)',
      numClass: 'text-cp-imp',
    },
    {
      title: 'Clean Core Compliance',
      value: `${(metrics?.clean_core_compliance_rate || 0).toFixed(1)}%`,
      icon: FileCheck,
      description: 'Compliance rate',
      accent: 'var(--teal)',
      numClass: 'text-cp-teal',
    },
  ];

  return (
    <div className="space-y-6" data-testid="dashboard">
      <div>
        <p className="text-[10.5px] font-semibold uppercase tracking-[0.1em] text-cp-faint mb-1">
          Control plane
        </p>
        <h1 className="text-[28px] font-bold tracking-tight text-cp-fg">Dashboard</h1>
        <p className="text-cp-muted mt-1.5">Welcome to SAP AI Customization Engine</p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {metricCards.map((metric, index) => {
          const Icon = metric.icon;
          return (
            <div
              key={index}
              className="cp-kpi"
              style={{ '--accent': metric.accent }}
              data-testid={`metric-card-${index}`}
            >
              <div className="flex items-center justify-between mb-3">
                <span className="label">{metric.title}</span>
                <Icon className={`h-4 w-4 ${metric.numClass}`} />
              </div>
              <div className={`num ${metric.numClass}`}>{metric.value}</div>
              <p className="mt-1 text-[11px] text-cp-faint">{metric.description}</p>
            </div>
          );
        })}
      </div>

      <div className="cp-card">
        <h2 className="text-[15px] font-bold text-cp-fg">Platform Overview</h2>
        <p className="text-[12.5px] text-cp-muted mt-1 mb-4">
          SAP AI Customization Engine
        </p>
        <div className="space-y-4">
          <div className="flex items-center justify-between pb-2 border-b border-cp-line">
            <span className="font-medium text-cp-fg">Average Delivery Time</span>
            <span className="text-cp-ok font-semibold tabular-nums">
              {metrics?.average_delivery_time || 0} days
            </span>
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-[10.5px] font-semibold uppercase tracking-[0.06em] text-cp-faint mb-1">
                Clean Core Principles
              </div>
              <div className="font-medium text-cp-fg">All customizations validated</div>
            </div>
            <div>
              <div className="text-[10.5px] font-semibold uppercase tracking-[0.06em] text-cp-faint mb-1">
                AI Agents
              </div>
              <div className="font-medium text-cp-fg">Design, Dev, Test, DevOps, Governance</div>
            </div>
            <div>
              <div className="text-[10.5px] font-semibold uppercase tracking-[0.06em] text-cp-faint mb-1">
                Integrations
              </div>
              <div className="font-medium text-cp-fg">Jira, SAP BTP</div>
            </div>
            <div>
              <div className="text-[10.5px] font-semibold uppercase tracking-[0.06em] text-cp-faint mb-1">
                Automation Level
              </div>
              <div className="font-medium text-cp-fg">70% reduction in manual effort</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
